import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Weight, MoveHorizontal, Flame } from "lucide-react";
import { ASSETS, VIDEOS } from "./assets";

const EASE = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 1 + i * 0.09, ease: EASE },
  }),
};

function MaskedLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function CornerTicks() {
  return (
    <>
      <span className="pointer-events-none absolute top-1.5 left-2.5 font-mono-tech text-xs text-white/40 z-10">+</span>
      <span className="pointer-events-none absolute top-1.5 right-2.5 font-mono-tech text-xs text-white/40 z-10">+</span>
      <span className="pointer-events-none absolute bottom-1.5 left-2.5 font-mono-tech text-xs text-white/40 z-10">+</span>
      <span className="pointer-events-none absolute bottom-1.5 right-2.5 font-mono-tech text-xs text-white/40 z-10">+</span>
    </>
  );
}

const SPOTLIGHT = {
  background:
    "radial-gradient(ellipse 75% 60% at 50% 40%, rgba(255,255,255,0.10) 0%, rgba(230,57,70,0.06) 45%, #000000 80%)",
};

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const yVideo = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yBento = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const yLogo = useTransform(scrollYProgress, [0, 1], [0, 120]);

  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const rotateX = useSpring(tiltX, { stiffness: 140, damping: 20 });
  const rotateY = useSpring(tiltY, { stiffness: 140, damping: 20 });

  const handleMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    tiltY.set(((e.clientX - r.left) / r.width - 0.5) * 6);
    tiltX.set(-((e.clientY - r.top) / r.height - 0.5) * 6);
  };
  const handleMouseLeave = () => {
    tiltX.set(0);
    tiltY.set(0);
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative overflow-hidden pt-28 pb-20 lg:pt-36 lg:pb-28"
    >
      <div className="absolute inset-0 blueprint-grid blueprint-grid-fade pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        {/* Brand signature — Atlas / Sapeur-360 (opening hook, wipe reveal) */}
        <motion.div
          data-testid="brand-signature"
          style={{ y: yLogo }}
          className="mb-10 lg:mb-16 flex justify-center relative left-1/2 -ml-[71vw] w-[142vw] sm:-ml-[65vw] sm:w-[130vw] lg:-ml-[50vw] lg:w-screen lg:px-6"
        >
          <motion.div
            initial={{ clipPath: "inset(0 100% 0 0)" }}
            animate={{ clipPath: "inset(0 0% 0 0)" }}
            transition={{ duration: 1.2, ease: EASE }}
            className="w-full"
          >
            <img src={ASSETS.logo} alt="Logo Sapeur-360 — Atlas portant le globe (KRX), amplitude de levage 13 à 45 cm, max 3000 kg" className="w-full max-w-none h-auto object-contain" />
          </motion.div>
        </motion.div>

        {/* Product showcase — autoplay video with parallax + 3D tilt */}
        <motion.div
          data-testid="hero-product-showcase"
          style={{ y: yVideo }}
          className="mb-16 lg:mb-24 flex justify-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.965, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ rotateX, rotateY, transformPerspective: 1200 }}
            className="relative w-full max-w-[60rem] mx-auto bg-black rounded-sm border border-zinc-200 overflow-hidden will-change-transform"
          >
            <video
              data-testid="hero-video"
              src={VIDEOS[0].src}
              poster={ASSETS.liftCar}
              autoPlay muted loop playsInline
              className="w-full h-auto object-contain bg-black"
            />
            <CornerTicks />
            <div className="absolute top-3 left-3 inline-flex items-center gap-2 bg-white/95 px-3 py-1.5 rounded-sm">
              <span className="h-2 w-2 rounded-full bg-[#E63946]" />
              <span className="font-mono-tech text-[11px] uppercase tracking-wider text-[#0A0A0A]">Sapeur-360 · En action</span>
            </div>
          </motion.div>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left content */}
          <div className="lg:col-span-6">
            <motion.div initial="hidden" animate="show" variants={fadeUp} custom={0}
              className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-3 py-1.5 rounded-sm">
              <span className="relative inline-block h-2 w-2">
                <span className="ping-soft absolute inset-0" />
                <span className="relative block h-2 w-2 rounded-full bg-[#E63946]" />
              </span>
              <span className="font-mono-tech text-xs uppercase tracking-[0.18em] text-[#52525B]">
                Plateforme robotisée sur chenilles · KRX
              </span>
            </motion.div>

            <h1 className="font-display mt-6 text-5xl md:text-6xl font-black tracking-tighter leading-[0.98]">
              <MaskedLine delay={0.55}>Soulevez, déplacez,</MaskedLine>
              <MaskedLine delay={0.7}>
                <span className="text-[#E63946]">éteignez.</span>
              </MaskedLine>
            </h1>

            <motion.p initial="hidden" animate="show" variants={fadeUp} custom={2}
              className="mt-6 text-lg leading-relaxed text-[#52525B] max-w-xl">
              Sapeur-360 est une plateforme robotisée sur chenilles qui se glisse sous n'importe quel
              véhicule, le soulève de <strong className="text-[#0A0A0A]">13 à 45 cm</strong> (jusqu'à
              <strong className="text-[#0A0A0A]"> 3 000 kg</strong>) et le déplace au centimètre près.
              En intervention, elle projette de l'eau pour maîtriser les incendies de véhicules — y
              compris électriques.
            </motion.p>

            <motion.div initial="hidden" animate="show" variants={fadeUp} custom={3}
              className="mt-8 flex flex-col sm:flex-row gap-3">
              <a data-testid="hero-demo-button" href="#contact"
                className="group inline-flex items-center justify-center gap-2 bg-[#E63946] hover:bg-[#BE123C] text-white font-bold px-7 py-3.5 rounded-sm transition-colors duration-300">
                Demander une démonstration
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a data-testid="hero-usecases-button" href="#cas-usage"
                className="inline-flex items-center justify-center gap-2 border border-zinc-300 hover:border-[#0A0A0A] text-[#0A0A0A] font-bold px-7 py-3.5 rounded-sm transition-colors duration-300">
                Voir les cas d'usage
              </a>
            </motion.div>

            <motion.div initial="hidden" animate="show" variants={fadeUp} custom={4}
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#52525B]">
              <span className="inline-flex items-center gap-2"><Weight className="h-4 w-4 text-[#E63946]" /> Jusqu'à 3 000 kg</span>
              <span className="inline-flex items-center gap-2"><MoveHorizontal className="h-4 w-4 text-[#E63946]" /> Levage 13–45 cm</span>
              <span className="inline-flex items-center gap-2"><Flame className="h-4 w-4 text-[#E63946]" /> Extinction intégrée</span>
            </motion.div>
          </div>

          {/* Right bento — real product images with spotlight staging + parallax */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
            style={{ y: yBento }}
            className="lg:col-span-6"
          >
            <div className="grid grid-cols-2 grid-rows-2 gap-3 h-[460px]">
              <div className="col-span-2 group relative rounded-sm overflow-hidden border border-zinc-200 bg-black" style={SPOTLIGHT}>
                <img src={ASSETS.product} alt="Plateforme Sapeur-360 KRX"
                  className="absolute inset-0 h-full w-full object-contain p-2 transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
                <CornerTicks />
                <div className="absolute top-3 left-3 inline-flex items-center gap-2 bg-white/95 px-3 py-1.5 rounded-sm">
                  <span className="h-2 w-2 rounded-full bg-[#E63946]" />
                  <span className="font-mono-tech text-[11px] uppercase tracking-wider text-[#0A0A0A]">Sapeur-360 · Plateforme à chenilles</span>
                </div>
              </div>
              <div className="group relative rounded-sm overflow-hidden border border-zinc-200 bg-black">
                <img src={ASSETS.fireSuppression} alt="Extinction d'un feu de véhicule"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]" />
                <span className="absolute bottom-2 left-2 font-mono-tech text-[10px] uppercase tracking-wider text-white bg-black/60 px-2 py-1 rounded-sm">Extinction</span>
              </div>
              <div className="group relative rounded-sm overflow-hidden border border-zinc-200 bg-black">
                <img src={ASSETS.underVehicle} alt="Levage d'un véhicule par le châssis"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]" />
                <span className="absolute bottom-2 left-2 font-mono-tech text-[10px] uppercase tracking-wider text-white bg-black/60 px-2 py-1 rounded-sm">Levage</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.div
          data-testid="scroll-cue"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.9, duration: 0.8 }}
          className="mt-16 lg:mt-20 flex flex-col items-center gap-3"
        >
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">Défiler</span>
          <div className="h-10 w-px bg-zinc-300 overflow-hidden">
            <motion.div
              className="h-full w-full bg-[#E63946]"
              animate={{ y: ["-100%", "100%"] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
